import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { X as Close } from '@styled-icons/bootstrap/X';
import { useImpactClaims } from 'hooks';
import ImpactClaimsSlider from './ImpactClaimSlider';
import { ImpactClaimDefinition } from 'components/Shared/ImpactClaims';
import { CheckList, SdgPills, SubTitle } from 'components/Shared';
import { useTranslation } from 'react-i18next';
import DocumentItem from './DocumentItem';
import { useSpring } from '@react-spring/core';
import { animated } from '@react-spring/web';
import Logo from 'images/seedtrace_logo_white.svg';
import OpenDetailsButton from 'components/Shared/OpenDetailsButton';
const ModalContainer = styled.div `
  position: relative;
  width: 100%;
  font-family: Lato;
`;
const CloseButton = styled.button `
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  transform: translate3d(16px, -80%, 0);
  background: transparent !important;
  border: none !important;
  outline: none !important;
  cursor: pointer !important;
  padding: 8px !important;
  z-index: 9999;
`;
const SliderContainer = styled.div `
  padding: ${({ isMobile }) => (isMobile ? '20px 0 30px' : '20px 28px 30px')};
  background-color: ${({ theme }) => theme.backgrounds.light};
  border-radius: 4px;
`;
const ClaimContainer = styled.div `
  border-radius: 4px;
  margin: 0;
  max-width: none;
`;
const StyledClose = styled(Close) `
  color: #fff;
`;
const FlexTitleContainer = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
`;
const SdgPillsContainer = styled.div `
  padding-bottom: 16px;
`;
const Button = styled.button `
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 20px;
  border: none;
  border-radius: 4px;
  margin: -2px 0;
  cursor: pointer;
`;
const Row = styled.div `
  display: flex;
  gap: 32px;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;
const OverviewRow = styled(Row) `
  padding-bottom: 16px;
`;
const Column = styled.div `
  max-width: ${({ isMobile }) => (isMobile ? '100%' : '50%')};
  width: 100%;
`;
const DetailsContainer = styled(animated.div) `
  position: relative;
  overflow: hidden;
`;
const DocumentShadow = styled.div `
  display: flex;
  justify-content: flex;
  align-items: center;
  background: ${({ theme }) => theme.palette.grey[50]};
  box-shadow: ${({ theme }) => theme.shadows[0]};
  border-left: 8px solid transparent;
  border-radius: 6px;
  padding: 16px;
  text-decoration: none;
  overflow: hidden;
  margin: ${({ spacing }) => `0 ${spacing}px -16px`};
`;
const ScrollContainer = styled.div `
  max-height: calc(100vh - 290px);
  overflow: auto;
  padding: 28px 28px 0;
`;
const LogoContainer = styled.div `
  position: absolute;
  bottom: -30px !important;
  right: 0 !important;
  transform: translateY(calc(100% + 4px));
  width: 80px;
  height: 30px;

  a {
    text-decoration: none !important;
  }
`;
const MAX_DOCUMENTS = 1;
const ClaimModalContent = ({ isMobile }) => {
    var _a, _b, _c, _d, _e;
    const { selectedClaim, handleCloseClaim } = useImpactClaims();
    const [openDetails, setOpenDetails] = useState(false);
    const { t } = useTranslation();
    const hasMultipleAttachments = (selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.attachmentCount) > MAX_DOCUMENTS;
    const styles = useSpring({
        maxHeight: openDetails ? 2000 : 0,
    });
    return (React.createElement(ModalContainer, null,
        React.createElement(SliderContainer, { isMobile: isMobile },
            React.createElement(ImpactClaimsSlider, { backgroundColor: "#F9F9F9", isMobile: isMobile })),
        React.createElement(ClaimContainer, null,
            React.createElement(ScrollContainer, null,
                React.createElement(OverviewRow, { isMobile: isMobile },
                    React.createElement(Column, { isMobile: isMobile },
                        React.createElement(ImpactClaimDefinition, { impactClaim: selectedClaim })),
                    React.createElement(Column, { isMobile: isMobile },
                        React.createElement(FlexTitleContainer, null,
                            React.createElement(SubTitle, { includeMargin: false }, t('impact-claims.proof-documents-title')),
                            hasMultipleAttachments && !openDetails && (React.createElement(Button, { onClick: () => setOpenDetails(!openDetails) }, t('impact-claims.see-all-documents')))),
                        hasMultipleAttachments && !openDetails ? (React.createElement(Fragment, null,
                            React.createElement(DocumentShadow, { spacing: 32 }),
                            React.createElement(DocumentShadow, { spacing: 16 }),
                            React.createElement(DocumentItem, { key: (_c = (_b = (_a = selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.attachments) === null || _a === void 0 ? void 0 : _a.edges[0]) === null || _b === void 0 ? void 0 : _b.node) === null || _c === void 0 ? void 0 : _c.id, document: (_e = (_d = selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.attachments) === null || _d === void 0 ? void 0 : _d.edges[0]) === null || _e === void 0 ? void 0 : _e.node }))) : (selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.attachments.edges.map(({ node }) => (React.createElement(DocumentItem, { key: node.id, document: node })))))),
                React.createElement(DetailsContainer, { style: styles },
                    React.createElement(Row, { isMobile: isMobile },
                        React.createElement(Column, { isMobile: isMobile },
                            (selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.impactCatalogItem.sustainableDevGoals.length) !==
                                0 && (React.createElement(SdgPillsContainer, null,
                                React.createElement(SubTitle, null, t('impact-claims.connected-global-goals')),
                                React.createElement(SdgPills, { sdgs: (selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.impactCatalogItem.sustainableDevGoals) ||
                                        [] }))),
                            React.createElement(SubTitle, { tooltip: t('impact-claims.criteria-info', {
                                    impactTitle: selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.impactCatalogItem.title.toLowerCase(),
                                }) }, t('impact-claims.criteria')),
                            React.createElement(CheckList, { items: (selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.impactCatalogItem.acceptanceCriteria.map(({ description }) => description)) || [] })),
                        React.createElement(Column, { isMobile: isMobile },
                            React.createElement(SubTitle, { tooltip: t('impact-claims.criteria-document-info', {
                                    impactTitle: selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.impactCatalogItem.title.toLowerCase(),
                                }) }, t('impact-claims.documentCriteria')),
                            React.createElement(CheckList, { items: (selectedClaim === null || selectedClaim === void 0 ? void 0 : selectedClaim.impactCatalogItem.docRequirements.map(({ description }) => description)) || [] }))))),
            React.createElement(OpenDetailsButton, { toggle: () => setOpenDetails(!openDetails), open: openDetails })),
        React.createElement(CloseButton, { onClick: handleCloseClaim },
            React.createElement(StyledClose, { size: 30 })),
        React.createElement(LogoContainer, null,
            React.createElement("a", { href: "https://seedtrace.org", target: "_blank", rel: "noopener noreferrer" },
                React.createElement(Logo, null)))));
};
export default ClaimModalContent;
